import {ValueDispatcher} from '@cascade/core';
import {PlaybackState} from '@cascade/core';
import type {RemotionSceneDescription} from '@cascade/core';
import {RemotionSceneWrapper} from './RemotionSceneWrapper';

/**
 * Drop-in replacement for MC's PlaybackManager that derives all state
 * from Remotion's frame counter instead of running generator coroutines.
 *
 * The UI reads:
 *  - `onSceneChanged` (useCurrentScene)
 *  - `onScenesRecalculated` (useScenes, SceneTrack)
 *  - `frame`, `fps`, `duration`, `speed`, `state`, `finished`
 *  - `currentScene`, `previousScene`
 */
export class RemotionPlaybackManager {
  // ── Dispatchers the UI subscribes to ─────────────────────────────────
  public get onSceneChanged() {
    return this._currentScene.subscribable;
  }
  private readonly _currentScene = new ValueDispatcher<RemotionSceneWrapper | null>(null);

  public get onScenesRecalculated() {
    return this._scenes.subscribable;
  }
  private readonly _scenes = new ValueDispatcher<RemotionSceneWrapper[]>([]);

  // ── Mutable fields read by PlaybackStatus ────────────────────────────
  public frame = 0;
  public speed = 1;
  public fps: number;
  public duration = 0;
  public finished = false;
  public state = PlaybackState.Paused;
  public slides: never[] = [];

  /** All scene wrappers with computed frame offsets. */
  public get scenes(): RemotionSceneWrapper[] {
    return this._scenes.current;
  }

  public get currentScene(): RemotionSceneWrapper | null {
    return this._currentScene.current;
  }
  public set currentScene(scene: RemotionSceneWrapper | null) {
    this._currentScene.current = scene;
  }

  public previousScene: RemotionSceneWrapper | null = null;

  constructor(fps: number) {
    this.fps = fps;
  }

  /**
   * Build scene wrappers from Remotion scene descriptions and compute
   * cumulative frame offsets.
   */
  public setupFromDescriptions(scenes: RemotionSceneDescription[]) {
    let firstFrame = 0;
    const wrappers: RemotionSceneWrapper[] = [];

    for (const desc of scenes) {
      const wrapper = new RemotionSceneWrapper({
        name: desc.name,
        fps: desc.fps,
        durationInFrames: desc.durationInFrames,
        component: desc.component as () => Promise<{default: React.ComponentType}>,
        firstFrame,
      });
      wrappers.push(wrapper);
      firstFrame += desc.durationInFrames;
    }

    this.duration = firstFrame;
    this._scenes.current = wrappers;
    this._currentScene.current = wrappers[0] ?? null;
  }

  /**
   * Called on every frame update from Remotion. Determines which scene
   * is active based on frame position and fires onSceneChanged if needed.
   */
  public syncFrame(frame: number) {
    this.frame = frame;
    this.finished = frame >= this.duration;

    const scenes = this._scenes.current;
    if (scenes.length === 0) return;

    // Find which scene this frame belongs to
    let active: RemotionSceneWrapper = scenes[0];
    for (const scene of scenes) {
      if (frame >= scene.firstFrame) {
        active = scene;
      } else {
        break;
      }
    }

    if (active !== this._currentScene.current) {
      this._currentScene.current = active;
    }
  }

  // ── Stubs for PlaybackManager methods the UI/player might call ──────
  public setup(scenes: RemotionSceneWrapper[]) {
    this._scenes.current = scenes;
    this._currentScene.current = scenes[0] ?? null;
  }

  public reorderScenes(_fromIndex: number, _toIndex: number) {
    // No-op for Remotion
  }

  public removeScene(_index: number) {
    // No-op for Remotion
  }

  public async recalculate() {
    // Remotion scenes have fixed durations; nothing to recalculate
  }

  public async seek(frame: number) {
    this.syncFrame(frame);
    return this.finished;
  }

  public async progress() {
    this.frame += this.speed;
    this.finished = this.frame >= this.duration;
    return this.finished;
  }

  public async reset() {
    this.frame = 0;
    this.finished = false;
    this._currentScene.current = this._scenes.current[0] ?? null;
  }

  public reload() {
    // No-op for Remotion
  }
}
